import * as Sentry from '@sentry/react';
import type { Query } from '@tanstack/react-query';
import type { CollectionScoringSummaryView, CustomBenchmark, PairScoreView } from '@vizit/api';
import type { GetCollectionImagePairScoreQuery, GetCollectionImagePairScoreQueryVariables } from '@vizit/api';
import { COLLECTION_SCORING_WINDOW_MS, pollWhilePending, readPairScore } from '@vizit/api';
import { APIError, useGetBenchmarkQuery, useListCollectionScoringsQuery } from '@vizit/api';
import { useGetCollectionImagePairScoreQuery, useGetCollectionQuery } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS, UNIVERSAL_BENCHMARK_KEY } from '@vizit/constants';
import { useCustomBenchmarksAvailability, useShowUniversalBenchmarks } from '@vizit/feature-flags';
import * as React from 'react';

interface UseCollectionPairScoreProps {
  workspaceId: string;
  /** The collection the image was opened from, when any; without it there is no pair */
  collectionId?: string;
  /**
   * The custom benchmark the collection showed with no category under it, as a picker key (`custom:<id>`). Until the
   * collection's own pair is under it there is no pair: the collection's would be another benchmark's score.
   */
  awaitingCategoryBenchmarkKey?: string;
  imageId: string;
  /**
   * When the image was created, by the API's clock. An upload after the pair's request starts the run's window, as
   * the API requests the pair again for it from its landing
   */
  imageCreatedAt?: string | null;
}

interface UseCollectionPairScoreResult {
  /** The flag and the API both have custom benchmarks on for the workspace's organisation */
  customBenchmarksEnabled: boolean;
  /** The API has not said yet whether custom benchmarks are on for the workspace's organisation */
  isCheckingCustomBenchmarks: boolean;
  /** Custom benchmarks are on and the image was opened from a collection */
  hasCollectionContext: boolean;
  /**
   * The collection's selected pair; `null` without one, until the collection loads, for Universal flagged off, or while
   * awaiting a category
   */
  pair: CollectionScoringSummaryView | null;
  /** The custom benchmark the collection showed with no category, while its own pair is not under it */
  awaitingCategoryBenchmark: CustomBenchmark | null;
  /** The image's score under `pair`, `undefined` until read */
  pairScore: PairScoreView | null | undefined;
  /** The collection, whether custom benchmarks apply to it, or the benchmark it showed with no category is loading */
  isCollectionLoading: boolean;
  /** The collection, or whether the benchmark it showed still has no category, failed to load */
  isCollectionError: boolean;
  isPairScoreLoading: boolean;
  isPairScoreError: boolean;
  /** When the pair score was last read, by the page's clock */
  pairScoreReadAt: number;
  /** The pair score was read under an earlier request than the pair's current one, and is being read again */
  isPairScoreReadBeforeRequest: boolean;
  /** When the run the image waits on started: the pair's request, or the image's upload when that came later */
  runStartedAt: string | null;
  /** Reads the pair score again, e.g. once something the page sees says it changed */
  refetchPairScore: () => void;
}

type PairScoreQuery = Query<GetCollectionImagePairScoreQuery, unknown, GetCollectionImagePairScoreQuery>;

/** When the run the image waits on started: the pair's request, or the image's upload when that came later. */
function toRunStartedAt(requestedAt: string, imageCreatedAt: string | null | undefined): string {
  return imageCreatedAt && Date.parse(imageCreatedAt) > Date.parse(requestedAt) ? imageCreatedAt : requestedAt;
}

/**
 * Re-reads the pair score while the pair's run is pending for the image, or while the read predates the pair's current
 * request. The asset page is its own route, so the collection page's scoring poll and its landing refresh are not
 * there to do it. Watched for the run's window from its start. An upload the pair has not been requested again for is
 * not polled here: the collection's request time, and the image's own score, say when to read it again.
 */
function pairScoreRefetchInterval(
  runStartedAt: string,
  variables: GetCollectionImagePairScoreQueryVariables,
  requestedAt: string,
) {
  return pollWhilePending<PairScoreQuery>(
    (query) => {
      const read = query.state.data;
      if (read === undefined) {
        return [];
      }
      // A read from before the current request is read again until a read under it lands, a failed one included.
      const isReadBeforeRequest = read.requestedAt !== requestedAt;
      return isReadBeforeRequest || readPairScore(read.pairScore).isScoring ? [runStartedAt] : [];
    },
    {
      staleTimeoutMs: COLLECTION_SCORING_WINDOW_MS,
      onTimeout: () => {
        Sentry.captureException(new Error('Asset pair score still scoring after the collection scoring window.'), {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Collection,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionScoringPolling,
          },
          extra: { ...variables, runStartedAt },
        });
      },
    },
  );
}

/**
 * The collection's selected pair and the image's score under it, polled while the pair's run is still on it. Under
 * custom benchmarks the pair an asset was scored against is the collection's selected pair, and the asset's score for
 * it comes from the collection images listing filtered to that pair. The asset header and the Spark gate both read it
 * here, so they agree and the poll runs whichever of them is mounted.
 */
function useCollectionPairScore(props: UseCollectionPairScoreProps): UseCollectionPairScoreResult {
  const { workspaceId, collectionId, awaitingCategoryBenchmarkKey, imageId, imageCreatedAt } = props;

  const benchmarksAvailability = useCustomBenchmarksAvailability(workspaceId);
  const showUniversalBenchmarks = useShowUniversalBenchmarks();
  const customBenchmarksEnabled = benchmarksAvailability.isEnabled;
  // A disabled query still serves cached data, so the flag gates the pair itself, not only the read.
  const hasCollectionContext = customBenchmarksEnabled && !!workspaceId && !!collectionId;
  // Until the API answers, an image opened from a collection may yet be scored by the collection's pair.
  const isCheckingCollectionContext = benchmarksAvailability.isChecking && !!workspaceId && !!collectionId;
  const {
    data: collection,
    isLoading: isCollectionLoading,
    isError: isCollectionError,
  } = useGetCollectionQuery(
    { workspace_id: workspaceId, collection_id: collectionId ?? '' },
    { enabled: hasCollectionContext },
  );
  const lastScoring = hasCollectionContext ? (collection?.last_scoring ?? null) : null;
  // The benchmark the collection showed with no category stands while the collection's own pair is another one's and
  // the collection has no category under it, as the collection page reads it. One that is gone (deleted) is dropped
  // silently, leaving the collection's pair; one that fails to read keeps that pair hidden, as the collection does.
  const [keyKind, awaitingId] = awaitingCategoryBenchmarkKey?.split(/:(.*)/s) ?? [];
  const isAwaitingCandidate =
    hasCollectionContext &&
    keyKind === 'custom' &&
    !!awaitingId &&
    !!collection &&
    lastScoring?.benchmark_id !== awaitingId;
  const {
    data: awaitingBenchmark,
    isLoading: isAwaitingBenchmarkLoading,
    error: awaitingBenchmarkError,
  } = useGetBenchmarkQuery(
    { workspace_id: workspaceId, benchmark_id: awaitingId ?? '' },
    { enabled: isAwaitingCandidate },
  );
  const {
    data: awaitingScorings,
    isLoading: isAwaitingScoringsLoading,
    isError: isAwaitingScoringsError,
  } = useListCollectionScoringsQuery(
    { workspace_id: workspaceId, collection_id: collectionId ?? '', benchmark_id: awaitingId ?? null, retailer: null },
    { enabled: isAwaitingCandidate },
  );
  const isAwaitingBenchmarkGone = awaitingBenchmarkError instanceof APIError && awaitingBenchmarkError.status === 404;
  const isAwaiting = isAwaitingCandidate && !isAwaitingBenchmarkGone && !awaitingScorings?.length;
  const isAwaitingError =
    isAwaiting && ((!!awaitingBenchmarkError && !isAwaitingBenchmarkGone) || isAwaitingScoringsError);
  // A Universal pair hidden by the flag is no pair here, as the collection page falls back from it too.
  const isHiddenUniversal = lastScoring?.retailer === UNIVERSAL_BENCHMARK_KEY && !showUniversalBenchmarks;
  const pair = isAwaiting || isHiddenUniversal ? null : lastScoring;
  const isCustomPair = !!pair && !pair.retailer;

  const pairScoreVariables: GetCollectionImagePairScoreQueryVariables = {
    workspace_id: workspaceId,
    collection_id: collectionId ?? '',
    image_id: imageId,
    product_category_id: pair?.product_category_id ?? '',
    benchmark_id: isCustomPair ? pair.benchmark_id : undefined,
    retailer: pair?.retailer ?? undefined,
  };
  const runStartedAt = pair ? toRunStartedAt(pair.requested_at, imageCreatedAt) : null;
  const {
    data: pairScoreRead,
    isLoading: isPairScoreLoading,
    isError: isPairScoreError,
    dataUpdatedAt: pairScoreReadAt,
    refetch,
  } = useGetCollectionImagePairScoreQuery(pairScoreVariables, {
    enabled: !!pair,
    requestedAt: pair?.requested_at,
    refetchInterval:
      pair && runStartedAt ? pairScoreRefetchInterval(runStartedAt, pairScoreVariables, pair.requested_at) : false,
  });
  const pairScore = pairScoreRead?.pairScore;
  // A request after the read reopens the run, which may not have reached the image yet, so until the next read only a
  // score from it counts.
  const isPairScoreReadBeforeRequest =
    !!pair && pairScoreRead !== undefined && pairScoreRead.requestedAt !== pair.requested_at;
  const refetchPairScore = React.useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    customBenchmarksEnabled,
    isCheckingCustomBenchmarks: benchmarksAvailability.isChecking,
    hasCollectionContext,
    pair,
    awaitingCategoryBenchmark: isAwaiting ? (awaitingBenchmark ?? null) : null,
    pairScore,
    isCollectionLoading:
      isCheckingCollectionContext ||
      (hasCollectionContext && isCollectionLoading) ||
      (isAwaitingCandidate && (isAwaitingBenchmarkLoading || isAwaitingScoringsLoading)),
    isCollectionError: hasCollectionContext && (isCollectionError || isAwaitingError),
    isPairScoreLoading: !!pair && isPairScoreLoading,
    isPairScoreError,
    pairScoreReadAt,
    isPairScoreReadBeforeRequest,
    runStartedAt,
    refetchPairScore,
  };
}

export { useCollectionPairScore };
export type { UseCollectionPairScoreProps, UseCollectionPairScoreResult };
